import { useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useLogger } from 'hooks/useLogger/useLogger';
import {
  sendForgotPasswordEmail,
  updatePassword,
  updateTemporaryPassword,
} from 'services/amplify';

import passwordValidationRules from './passwordValidationRules';

import { CreatePasswordProps } from './types';

import * as S from '../styles';
import * as SS from './styles';

const CreatePassword: React.FC<CreatePasswordProps> = ({
  user,
  email,
  isFirstLogin,
  onPasswordCreated,
}) => {
  const mountedRef = useRef(true);

  const intl = useIntl();
  const logger = useLogger();

  const [newPassword, setNewPassword] = useState('');
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordDisabled, setPasswordDisabled] = useState(isFirstLogin);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [createPasswordLoading, setCreatePasswordLoading] = useState(false);
  const [confirmPasswordDisabled, setConfirmPasswordDisabled] = useState(true);
  const [validatedRules, setValidatesRule] = useState(
    passwordValidationRules('').map(rule => {
      return {
        name: rule.name,
        valid: false,
      };
    }),
  );

  const handleChangeConfirmationCode = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmationCode(e.target.value);
  };

  const handleChangeNewPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordError(false);
    setNewPassword(e.target.value);
  };

  const handleChangeConfirmNewPassword = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmNewPassword(e.target.value);
  };

  const handleCreateNewPassword = async () => {
    setCreatePasswordLoading(true);

    if (mountedRef.current) {
      try {
        let data;
        if (isFirstLogin) {
          data = await updateTemporaryPassword(user, confirmNewPassword);
        } else {
          data = await updatePassword(
            email,
            confirmationCode,
            confirmNewPassword,
          );
        }

        if (data) onPasswordCreated();
        setPasswordError(true);
      } catch (e) {
        setPasswordError(true);
        logger.error('create new password', { e });
      }
    }

    setCreatePasswordLoading(false);
  };

  const handleResendEmail = async () => {
    if (mountedRef.current) {
      try {
        await sendForgotPasswordEmail(email);
      } catch (e) {
        logger.error('send login recovery password email', { e });
      }
    }
  };

  const handleActionKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    const keyCode = e.which || e.key;

    if (keyCode === 13 && !buttonDisabled) {
      e.preventDefault();
      handleCreateNewPassword();
    }
  };

  useEffect(() => {
    setPasswordDisabled(confirmationCode.length !== 6);
  }, [confirmationCode]);

  useEffect(() => {
    const updatedPasswordValidationRules = passwordValidationRules(
      newPassword,
    ).map(rule => {
      return {
        name: rule.name,
        valid: rule.validator(),
      };
    });

    setValidatesRule(updatedPasswordValidationRules);

    setConfirmPasswordDisabled(
      updatedPasswordValidationRules.some(rule => rule.valid !== true),
    );
  }, [newPassword]);

  useEffect(() => {
    if (
      newPassword.length &&
      newPassword.length === confirmNewPassword.length
    ) {
      if (newPassword === confirmNewPassword) {
        setButtonDisabled(false);
        setConfirmPasswordError(false);
      } else {
        setConfirmPasswordError(true);
        setButtonDisabled(true);
      }
    } else {
      setConfirmPasswordError(false);
      setButtonDisabled(true);
    }
  }, [confirmNewPassword, newPassword]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <div data-testid="createPasswordStep">
      <S.Title>
        <FormattedMessage
          id={`LOGIN.CREATE_PASSWORD.TITLE.${
            isFirstLogin ? 'NORMAL_LOGIN' : 'FIRST_LOGIN'
          }`}
        />
      </S.Title>

      <S.Form onKeyDown={handleActionKeyDown}>
        {!isFirstLogin && (
          <S.LoginInput
            full
            value={confirmationCode}
            autoComplete="one-time-code"
            data-testid="confirmationCodeInput"
            onChange={handleChangeConfirmationCode}
            label={intl.formatMessage({
              id: 'LOGIN.CREATE_PASSWORD.CONFIRMATION_CODE.LABEL',
            })}
          />
        )}

        <S.LoginPasswordInput
          full
          type="password"
          value={newPassword}
          autoComplete="new-password"
          data-testid="createPasswordInput"
          onChange={handleChangeNewPassword}
          disabled={!isFirstLogin && passwordDisabled}
          label={intl.formatMessage({
            id: `LOGIN.CREATE_PASSWORD.PASSWORD.LABEL.${
              isFirstLogin ? 'CREATE' : 'DEFINE'
            }`,
          })}
          error={
            passwordError
              ? intl.formatMessage({
                  id: 'LOGIN.CREATE_PASSWORD.PASSWORD.ERROR',
                })
              : ''
          }
        />
        <SS.ListRules>
          {validatedRules.map(rule => {
            return (
              <SS.ItemRule key={rule.name}>
                <SS.ValidationBullet valid={rule.valid} />
                <SS.Rule valid={rule.valid}>
                  <FormattedMessage id={rule.name} />
                </SS.Rule>
              </SS.ItemRule>
            );
          })}
        </SS.ListRules>

        <S.LoginPasswordInput
          full
          type="password"
          value={confirmNewPassword}
          autoComplete="new-password"
          data-testid="confirmPasswordInput"
          disabled={confirmPasswordDisabled}
          onChange={handleChangeConfirmNewPassword}
          label={intl.formatMessage({
            id: `LOGIN.CREATE_PASSWORD.CONFIRM_PASSWORD.LABEL.${
              isFirstLogin ? 'CREATE' : 'DEFINE'
            }`,
          })}
          error={
            confirmPasswordError
              ? intl.formatMessage({
                  id: 'LOGIN.CREATE_PASSWORD.CONFIRM_PASSWORD.ERROR',
                })
              : ''
          }
        />
      </S.Form>

      {!isFirstLogin && (
        <SS.ResendEmailContainer>
          <FormattedMessage id="LOGIN.CREATE_PASSWORD.PASSWORD_RECOVERY.LABEL" />
          <S.TextButton
            onClick={handleResendEmail}
            data-testid="resendEmailActionButton"
          >
            <FormattedMessage id="LOGIN.CREATE_PASSWORD.PASSWORD_RECOVERY.BUTTON" />
          </S.TextButton>
        </SS.ResendEmailContainer>
      )}

      <S.ActionButtonContainer>
        <S.ActionButton
          disabled={buttonDisabled}
          loading={createPasswordLoading}
          onClick={handleCreateNewPassword}
          data-testid="createPasswordActionButton"
        >
          <FormattedMessage
            id={`LOGIN.CREATE_PASSWORD.ACTION.${
              isFirstLogin ? 'CREATE' : 'DEFINE'
            }`}
          />
        </S.ActionButton>
      </S.ActionButtonContainer>
    </div>
  );
};

export default CreatePassword;
